import {useCallback, useEffect, useRef} from 'react';

import type {EagGridStore} from '../store/createEagGridStore';
import {useLatestRef} from './useLatestRef';

const STYLE_PREFIX = 'eag-grid-column-selection-style';

const SELECTION_BLUE = '#2196f3';
const RANGE_BG = `rgba(33, 150, 243, 0.15)`;

const ensureStyleElement = (id: string): HTMLStyleElement => {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  return el;
};

const clearStyleElement = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.textContent = '';
  }
};

const removeStyleElement = (id: string) => {
  document.getElementById(id)?.remove();
};

/** Scoped selector: targets cells only within the grid instance identified by aria-label. */
const cell = (instanceId: string, row: number, colKey: string) =>
  `table[aria-label="${CSS.escape(instanceId)}"] tr[aria-rowindex="${row}"] > td[data-column="${CSS.escape(colKey)}"]`;

/** Build CSS rules that highlight all cells in the selected range with a blue border perimeter. */
const applyRangeStyles = (
  styleId: string,
  instanceId: string,
  columnKeys: string[],
  minRow: number,
  maxRow: number
) => {
  if (columnKeys.length === 0) {
    clearStyleElement(styleId);
    return;
  }

  const allSelectors: string[] = [];
  const topBorder: string[] = [];
  const bottomBorder: string[] = [];
  const leftBorder: string[] = [];
  const rightBorder: string[] = [];

  const firstCol = columnKeys[0];
  const lastCol = columnKeys[columnKeys.length - 1];

  for (const key of columnKeys) {
    for (let i = minRow; i <= maxRow; i++) {
      const s = cell(instanceId, i, key);
      allSelectors.push(s);
      if (i === minRow) {
        topBorder.push(s);
      }
      if (i === maxRow) {
        bottomBorder.push(s);
      }
      if (key === firstCol) {
        leftBorder.push(s);
      }
      if (key === lastCol) {
        rightBorder.push(s);
      }
    }
  }

  const rules = [`${allSelectors.join(', ')} { background-color: ${RANGE_BG} !important; }`];
  if (topBorder.length > 0) {
    rules.push(`${topBorder.join(', ')} { border-top: 1px solid ${SELECTION_BLUE} !important; }`);
  }
  if (bottomBorder.length > 0) {
    rules.push(
      `${bottomBorder.join(', ')} { border-bottom: 1px solid ${SELECTION_BLUE} !important; }`
    );
  }
  if (leftBorder.length > 0) {
    rules.push(`${leftBorder.join(', ')} { border-left: 1px solid ${SELECTION_BLUE} !important; }`);
  }
  if (rightBorder.length > 0) {
    rules.push(
      `${rightBorder.join(', ')} { border-right: 1px solid ${SELECTION_BLUE} !important; }`
    );
  }

  ensureStyleElement(styleId).textContent = rules.join('\n');
};

/** Extract column key and row index from a DOM element at a given point. */
const getCellInfo = (
  target: EventTarget | Element | null
): {columnKey: string; rowIndex: number} | null => {
  const el = target as HTMLElement | null;
  if (!el?.closest) {
    return null;
  }
  const td = el.closest('td[data-column]');
  const tr = td?.closest('tr[aria-rowindex]');
  if (!td || !tr) {
    return null;
  }
  const columnKey = td.getAttribute('data-column');
  const rowIndex = Number(tr.getAttribute('aria-rowindex'));
  if (!columnKey || Number.isNaN(rowIndex)) {
    return null;
  }
  return {columnKey, rowIndex};
};

type UseColumnSelectionParams = {
  store: EagGridStore;
  enabled: boolean;
  /** Ordered list of visible column keys — used to compute column span during drag. */
  visibleColumnKeys: string[];
  /** Unique identifier used to scope the injected style elements (e.g. gridCode). */
  instanceId: string;
};

const SUPPRESS_CLICK_TIMEOUT = 200;

export const useColumnSelection = (params: UseColumnSelectionParams) => {
  const {store, enabled, instanceId} = params;
  // Use a ref so the mousemove/mouseup closures always read the latest keys
  const visibleColumnKeysRef = useLatestRef(params.visibleColumnKeys);

  const styleId = `${STYLE_PREFIX}-${instanceId}`;

  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const anchorCell = useRef<{columnKey: string; rowIndex: number} | null>(null);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: cleanup injected style elements on unmount
  useEffect(
    function removeStyleElementOnUnmount() {
      return () => {
        removeStyleElement(styleId);
      };
    },
    [styleId]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled || event.button !== 0) {
        return;
      }
      const cell = getCellInfo(event.target);
      if (!cell) {
        return;
      }
      // Prevent text selection during drag. Don't stopPropagation yet —
      // single clicks must still reach GridRow for row click navigation.
      event.preventDefault();

      isDragging.current = true;
      didDrag.current = false;
      anchorCell.current = cell;

      const buildRange = (
        anchor: {columnKey: string; rowIndex: number},
        current: {columnKey: string; rowIndex: number}
      ) => {
        const keys = visibleColumnKeysRef.current;
        const anchorIdx = keys.indexOf(anchor.columnKey);
        const currentIdx = keys.indexOf(current.columnKey);
        const minCol = Math.max(0, Math.min(anchorIdx, currentIdx));
        const maxCol = Math.min(keys.length - 1, Math.max(anchorIdx, currentIdx));
        return {
          columnKeys: keys.slice(minCol, maxCol + 1),
          minRow: Math.min(anchor.rowIndex, current.rowIndex),
          maxRow: Math.max(anchor.rowIndex, current.rowIndex),
        };
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current || !anchorCell.current) {
          return;
        }
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const current = getCellInfo(target);
        if (!current) {
          return;
        }
        // Only mark as dragged when the user moves to a DIFFERENT cell
        if (
          current.columnKey !== anchorCell.current.columnKey ||
          current.rowIndex !== anchorCell.current.rowIndex
        ) {
          didDrag.current = true;
        }
        const r = buildRange(anchorCell.current, current);
        applyRangeStyles(styleId, instanceId, r.columnKeys, r.minRow, r.maxRow);
      };

      const handleMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        if (!isDragging.current || !anchorCell.current) {
          isDragging.current = false;
          return;
        }
        isDragging.current = false;

        if (didDrag.current) {
          // Multi-cell drag: commit range and suppress the following click
          const target = document.elementFromPoint(e.clientX, e.clientY);
          const endCell = getCellInfo(target) ?? anchorCell.current;
          const finalRange = buildRange(anchorCell.current, endCell);
          store.setSelectedCellRange(finalRange);

          // Suppress the click event synthesized by the browser after mousedown+mouseup.
          // A timeout fallback ensures the listener is removed even if no click fires.
          const suppressClick = (ev: Event) => {
            ev.stopPropagation();
            ev.preventDefault();
            clearTimeout(fallbackTimer);
          };
          document.addEventListener('click', suppressClick, {capture: true, once: true});
          const fallbackTimer = setTimeout(() => {
            document.removeEventListener('click', suppressClick, {capture: true});
          }, SUPPRESS_CLICK_TIMEOUT);
        } else {
          // Single cell click: select just that cell but let the click bubble
          // through to GridRow for row click navigation.
          const singleRange = buildRange(anchorCell.current, anchorCell.current);
          store.setSelectedCellRange(singleRange);
          applyRangeStyles(
            styleId,
            instanceId,
            singleRange.columnKeys,
            singleRange.minRow,
            singleRange.maxRow
          );
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [enabled, store, visibleColumnKeysRef, styleId, instanceId]
  );

  const clearSelection = useCallback(() => {
    isDragging.current = false;
    anchorCell.current = null;
    clearStyleElement(styleId);
    store.setSelectedCellRange(null);
  }, [store, styleId]);

  // NOTE: the whole-column hover highlight that used to be injected here was
  // removed per T20-134467 OG feedback — a permanently highlighted column reads
  // as noise; the row hover is the only pointer-position affordance.

  return {handleMouseDown, clearSelection};
};
